import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Image, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { IoAddOutline, IoClose } from 'react-icons/io5';
import SelectDropdown from '../../common/utils/select-dropdown';
import { useAuth } from '../../../context/AuthContext';
import { collection, doc, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import { db, storage } from '../../../firebase';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { toast } from 'react-toastify';

const AddUpdateExpense = ({ showExpenseModal, setShowExpenseModal }) => {

  const { user } = useAuth();
  const { bid } = user.claims;

  const fileUploadRef = useRef(null);

  const [isAdding, setIsAdding] = useState(false);
  const [categories, setCategories] = useState(null);
  const [subCategories, setSubCategories] = useState(null);
  const [expenseData, setExpenseData] = useState({
    category: null,
    subCategory: null,
    description: '',
    date: null,
    amount: '',
    paymentMode: '',
    status: 'Paid',
    createdBy: user.uid ? user.uid : '',
  });
  const [attachment, setAttachment] = useState(null);

  const getSubCategories = (categoryID) => {
    const q = query(collection(db, "Businesses", bid, "ExpenseCategories", categoryID, "SubCategories"), orderBy('name'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempData = [];
      querySnapshot.forEach((doc) => {
        tempData.push({ id: doc.id, ...doc.data() });
      });

      setSubCategories(tempData);
    }, (error) => {
      console.log(error.message);
    });

    return () => unsubscribe;
  }

  const getCategories = () => {
    const q = query(collection(db, "Businesses", bid, "ExpenseCategories"), orderBy('name'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempData = [];
      querySnapshot.forEach((doc) => {
        tempData.push({ id: doc.id, ...doc.data() });
      });

      setCategories(tempData);
    }, (error) => {
      console.log(error.message);
    });

    return () => unsubscribe;
  }

  useEffect(() => {
    let d = new Date();
    if (d) {
      setExpenseData({ ...expenseData, date: d });
    }

    getCategories();
  }, [])

  async function handleExpenseData(expID, data) {
    data = {
      ...data,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    try {
      await setDoc(doc(db, "Businesses", bid, "Expenses", expID), data);
      setShowExpenseModal(false);
      toast.success("Expense created, Successfully.");
    } catch (error) {
      console.log("Add Expense Data [Error] : ", error);
    }
  }

  function handleValidateExpenseData(expenseData, file) {
    if (file) {
      const expID = uuidv4();
      const storageRef = ref(storage, `${bid}/Expenses/${expID}`);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on("state_changed", (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
      }, (err) => console.log('Expense Attachment File Uploading [Error] :', err),
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((url) => {
            expenseData.attachment = url;
            handleExpenseData(expID, expenseData);
          });
        }
      );
    } else {
      const expID = uuidv4();
      handleExpenseData(expID, expenseData);
    }
  }

  return (
    <Modal
      show={showExpenseModal}
      onHide={() => setShowExpenseModal(false)}
      size='lg'
      backdrop="static"
      keyboard={false}
      contentClassName='border-0 shadow-sm'
      centered
    >
      <div className='bg-light overflow-hidden rounded'>
        <div className='d-flex align-items-center justify-content-between'>
          {
            expenseData && expenseData.id ?
              <h5 className='mb-0 p-3'>Edit Expense</h5>
              :
              <h5 className='mb-0 p-3'>Create Expense</h5>
          }
          <Button
            variant='white'
            className='rounded border-0 px-3'
            onClick={() => {
              setExpenseData(null);
              setAttachment(null);
              setShowExpenseModal(false);
            }}
          >
            <IoClose size={20} />
          </Button>
        </div>
      </div>
      <div className='border-top border-bottom px-3 py-3'>
        <div className='row'>
          <div className='col-md-7'>
            <div className='row'>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Expense Category *</Form.Label>
                  <SelectDropdown
                    searchable
                    options={categories && categories}
                    labelField="name"
                    valueField="id"
                    placeholder="Select"
                    searchPlaceholder="Search by name"
                    selectedValue={expenseData && expenseData.category && expenseData.category.name}
                    onSelect={(value) => {
                      const obj = categories && categories.find((p) => p.id === value);
                      if (obj) {
                        getSubCategories(obj.id);
                      }
                      setExpenseData({ ...expenseData, category: obj })
                    }}
                    menuClass="shadow"
                  />
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Sub Category *</Form.Label>
                  <SelectDropdown
                    disabled={!subCategories || subCategories.length === 0}
                    searchable
                    options={subCategories && subCategories}
                    labelField="name"
                    valueField="id"
                    placeholder="Select"
                    searchPlaceholder="Search by name"
                    selectedValue={expenseData && expenseData.subCategory && expenseData.subCategory.name}
                    onSelect={(value) => {
                      const obj = subCategories && subCategories.find((p) => p.id === value);
                      setExpenseData({ ...expenseData, subCategory: obj });
                    }}
                    menuClass="shadow"
                  />
                </Form.Group>
              </div>
              <div className='col-md-12'>
                <Form.Group>
                  <Form.Label>Description <small>(Optional)</small></Form.Label>
                  <InputGroup className="mb-3">
                    <Form.Control
                      as={'textarea'}
                      rows={3}
                      placeholder="Expense description"
                      onChange={(event) => setExpenseData({ ...expenseData, description: event.target.value })}
                      value={expenseData && expenseData.description ? expenseData.description : ''}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group>
                  <Form.Label>Date *</Form.Label>
                  <InputGroup className="mb-3">
                    <Form.Control
                      type='date'
                      onChange={(event) => setExpenseData({ ...expenseData, date: new Date(event.target.value) })}
                      value={expenseData && expenseData.date ? format(new Date(expenseData.date), "yyyy-MM-dd") : ""}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Status *</Form.Label>
                  <SelectDropdown
                    searchable={false}
                    options={[
                      { name: "Paid" },
                      { name: "Unpaid" }
                    ]}
                    labelField="name"
                    valueField="name"
                    placeholder="Select"
                    selectedValue={expenseData && expenseData.status}
                    onSelect={(value) => setExpenseData({ ...expenseData, status: value })}
                    menuClass="shadow"
                  />
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group>
                  <Form.Label>Amount *</Form.Label>
                  <InputGroup className="mb-3">
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type='number'
                      placeholder="0"
                      className="border-left-0"
                      onChange={(event) => setExpenseData({ ...expenseData, amount: event.target.value })}
                      value={expenseData && expenseData.amount ? expenseData.amount : ''}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Mode of Payment *</Form.Label>
                  <SelectDropdown
                    searchable={false}
                    options={[
                      { name: "Cash" },
                      { name: "Bank Transfer" },
                      { name: "Credit Card" },
                      { name: "Debit Card" },
                      { name: "UPI" },
                      { name: "Cheque" }
                    ]}
                    labelField="name"
                    valueField="name"
                    placeholder="Select"
                    searchPlaceholder="Search by payment mode"
                    selectedValue={expenseData && expenseData.paymentMode}
                    onSelect={(value) => setExpenseData({ ...expenseData, paymentMode: value })}
                    menuClass="shadow"
                  />
                </Form.Group>
              </div>
            </div>
          </div>
          <div className='col-md-5'>
            <Form.Control type="file" accept="image/png, image/jpeg, image/jpg" ref={fileUploadRef} className='d-none' onChange={(event) => setAttachment(event.target.files[0])} />
            <div
              className='bg-light text-secondary border rounded p-3 h-100 d-flex flex-column align-items-center justify-content-center cursor-pointer'
              onClick={() => fileUploadRef.current.click()}
            >
              {
                attachment ?
                  <div style={{
                    width: "100%",
                    height: "100%",
                    maxHeight: "330px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Image src={attachment && typeof attachment === "string" ? attachment : URL.createObjectURL(attachment)} style={{ width: '100%', height: '100%', objectFit: "fill" }} />
                  </div>
                  :
                  <div className='text-center'>
                    <IoAddOutline size={28} />
                    <div className='mt-3'>Upload Document</div>
                    <p className='mb-0 fw-light'>
                      <small>(Expense invoice, receipt or any slip)</small>
                    </p>
                  </div>
              }
            </div>
          </div>
        </div>
      </div>
      <div className='d-flex align-items-center justify-content-end px-3 pb-3 mt-3'>
        <Button
          disabled={isAdding && isAdding}
          variant="light"
          onClick={() => {
            setExpenseData(null);
            setAttachment(null);
            setShowExpenseModal(false);
          }}
          className='border px-5 py-2 me-3'
        >
          Cancel
        </Button>
        <Button
          disabled={!expenseData || !expenseData.category || !expenseData.subCategory || !expenseData.date || !expenseData.status || !expenseData.amount || !expenseData.paymentMode || isAdding}
          variant="primary"
          className='px-5 py-2 btn-sm-width'
          onClick={() => handleValidateExpenseData(expenseData, attachment)}
        >
          {
            isAdding && isAdding ?
              <div>
                <Spinner size='sm' />
              </div>
              :
              <span>Save</span>
          }
        </Button>
      </div>
    </Modal>
  )
}

export default AddUpdateExpense;
